import { Building2, UserCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Badge } from "@/components/ui/badge";
import { useCurrentUserWorker } from "@/domains/workers/hooks/use-current-user-worker";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { getDisplayName } from "@/lib/utils/name-utils";

interface CurrentSalonRoleCardProps {
  className?: string;
}

export function CurrentSalonRoleCard({ className }: CurrentSalonRoleCardProps) {
  const { t } = useTranslation("settings");
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const getSalonRole = useSalonStore((state) => state.getSalonRole);
  const { workerProfile } = useCurrentUserWorker();
  const { user } = useAuthStore();

  // Don't render if no current salon
  if (!currentSalon) {
    return null;
  }

  const salonRole = getSalonRole(currentSalon.id);

  // Get display name with fallback to user's first and last name
  const displayName = getDisplayName(
    workerProfile?.name,
    { firstName: user?.firstName, lastName: user?.lastName },
    "Worker"
  );

  // Get role display information
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "OWNER":
        return "default"; // Gold-ish styling
      case "MANAGER":
        return "secondary"; // Blue-ish styling
      case "WORKER":
        return "outline"; // Green-ish styling
      default:
        return "outline";
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "OWNER":
        return "bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100";
      case "MANAGER":
        return "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100";
      case "WORKER":
        return "bg-green-50 border-green-200 text-green-700 hover:bg-green-100";
      default:
        return "";
    }
  };

  return (
    <SettingCard
      title={t("profile.salonRole.title")}
      description={t("profile.salonRole.description")}
      icon={<Building2 className="h-5 w-5" />}
      className={className}
    >
      <div className="space-y-4">
        {/* Worker Name - Show combined name or fallback */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            {t("profile.salonRole.workerName")}
          </label>
          <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
            <UserCheck className="h-4 w-4 text-muted-foreground" />
            <span>{displayName}</span>
          </div>
        </div>

        {/* Role */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            {t("profile.salonRole.role")}
          </label>
          <div className="flex items-center gap-2">
            <Badge
              variant={getRoleBadgeVariant(salonRole || "")}
              className={getRoleBadgeColor(salonRole || "")}
            >
              {salonRole && t(`profile.salonRole.roles.${salonRole}`, salonRole)}
            </Badge>
          </div>
        </div>
      </div>
    </SettingCard>
  );
}
